import { Box, Grid, Typography } from '@mui/material';
import {
  AutocompleteItem,
  AutocompleteMode,
  SearchBar,
  SearchBarVariant,
  SearchParams,
} from '@prismamedia/one-components';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { TagsProps } from '../../';
import {
  ArticleFormat,
  BrandKey,
  GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag,
  GetSearchTvCollectionQuery,
  GetSearchTvCollectionQueryVariables,
  GetSuggestedTagsListQuery_query_suggestTags_Tag,
  TagType,
  TvSearchContextType,
} from '../../../../__generated__/queries-web';
import { useSearchSuggestedTagsGetter } from '../../../../apollo/queries/getSuggestedTagsList.web.graphql';
import { TagItem } from '../../TagItem';
import { useEditableTag } from '../../useEditTag';
import {
  AutocompleteListHeader,
  getSuggestedTagsArticleParams,
} from '../../utils';
import { useStyles as useSharedStyles } from '../styles';
import {
  ALLOWED_COLLECTION_PROVIDERS,
  ALLOWED_COLLECTION_TAG_TYPES,
  ARTICLE_ENHANCEMENT_COLLECTION_TITLE,
  COLLECTION_SEARCH_BAR_PLACEHOLDER,
  NO_COLLECTION_SELECTED_TITLE,
  TAGS_COLLECTION_SECTION_DATA_TESTID,
} from './constants';
import { useLazyQuery } from '@apollo/client';
import { GET_SEARCH_TV_COLLECTION } from '../../../../apollo/queries/getSearchTvCollection.web.graphql';

interface TVCollectionsProps
  extends Pick<TagsProps, 'article' | 'onTagsChange'> {
  brandKey: BrandKey;
  field: string;
  suggestedTagList: GetSuggestedTagsListQuery_query_suggestTags_Tag[];
  tags: GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag[];
}

const TVCollections: FC<TVCollectionsProps> = ({
  article,
  brandKey,
  field,
  onTagsChange,
  tags: selectedTags,
}) => {
  const isFirstRender = useRef(true);
  const [getSearchTvCollection] = useLazyQuery<
    GetSearchTvCollectionQuery,
    GetSearchTvCollectionQueryVariables
  >(GET_SEARCH_TV_COLLECTION, {
    fetchPolicy: 'no-cache',
  });
  const searchSuggestedTags = useSearchSuggestedTagsGetter();

  const {
    newTags,
    handlers: { handleAddTag, handleDeleteTag },
  } = useEditableTag<
    GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag
  >(selectedTags, brandKey);

  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: '',
  });
  const [filteredSuggestedTagList, setFilteredSuggestedTagList] = useState<
    GetSuggestedTagsListQuery_query_suggestTags_Tag[]
  >([]);

  const selectedTVCollectionList = selectedTags.filter(
    ({ type }) => ALLOWED_COLLECTION_TAG_TYPES.indexOf(type) > -1,
  );

  const sharedClasses = useSharedStyles({
    selectedTagListLength: selectedTVCollectionList.length,
  });

  const getFilteredTVCollectionList = useCallback(
    async (search: string): Promise<AutocompleteItem[] | undefined> => {
      setSearchParams({ search });

      if (!search.length) {
        // Get suggested TV collection tags
        return searchSuggestedTags({
          article: getSuggestedTagsArticleParams(article),
          where: { type_in: ALLOWED_COLLECTION_PROVIDERS },
        }).then((data) => {
          const tagList = data || [];
          setFilteredSuggestedTagList(tagList);

          return tagList.map((tag) => ({
            label: tag.title,
            id: tag.id,
            title: tag.title,
            type: tag.type,
            relationId: tag.relationId,
          }));
        });
      }

      // Get TV collection tags based on field value
      // Get programs tags based on field value
      const tvCollectionList = await getSearchTvCollection({
        variables: {
          query: search,
          context:
            article?.format === ArticleFormat.Video
              ? TvSearchContextType.Video
              : TvSearchContextType.Rich,
        },
      });

      return tvCollectionList.data?.searchTvCollections.map((collection) => ({
        id: collection.id,
        label: collection.title,
        title: `${collection.title} {${collection.id}}`,
        type: TagType.TvCollection,
        relationId: collection.boneUUID,
      }));
    },
    [article, getSearchTvCollection, searchSuggestedTags],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      // eslint-disable-next-line immutable/no-mutation
      isFirstRender.current = false;
      return;
    }

    newTags && onTagsChange(newTags, field);
  }, [field, newTags, onTagsChange]);

  return (
    <Grid
      alignItems="flex-end"
      className={sharedClasses.wrapper}
      container
      data-testid={TAGS_COLLECTION_SECTION_DATA_TESTID}
      justifyContent="space-between"
    >
      <Grid item xs={4}>
        <Typography className={sharedClasses.subTitle} component="h3">
          {ARTICLE_ENHANCEMENT_COLLECTION_TITLE}{' '}
          <span>({selectedTVCollectionList.length})</span>
        </Typography>
      </Grid>

      <Grid item xs={8}>
        <SearchBar
          autocomplete={{
            fetchList: getFilteredTVCollectionList,
            listHeader: !searchParams?.search?.length ? (
              <AutocompleteListHeader
                listLength={filteredSuggestedTagList.length}
              />
            ) : undefined,
            mode: AutocompleteMode.MULTIPLE,
            onSelect: (value) => {
              value && handleAddTag(value);
            },
          }}
          fetchListMinValueLength={0}
          placeholder={COLLECTION_SEARCH_BAR_PLACEHOLDER}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          variant={SearchBarVariant.SECONDARY}
        />
      </Grid>
      <Box className={sharedClasses.innerWrapper} component="section">
        {selectedTVCollectionList.map((collection) => (
          <TagItem
            {...collection}
            key={collection.title}
            onDelete={handleDeleteTag}
          />
        ))}

        {!selectedTVCollectionList.length && (
          <Typography component="p">{NO_COLLECTION_SELECTED_TITLE}</Typography>
        )}
      </Box>
    </Grid>
  );
};

export { TVCollections };
